import { Component, OnInit } from '@angular/core';
import { Payment } from 'src/app/Annual-Details/Taxes/payment.model';
import { IWeissmanModalComponent } from 'src/app/Compliance/WeissmanModalService';
import { AttachmentService } from '../attachment.service';
import { EntityType } from 'src/app/constants.new';
import { find, map } from 'lodash';
import { Attachment } from '../attachment.model';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { DocumentService } from 'src/app/Documents/document.service';

export interface BillImageModalParams {
    billId: number;
    payment: Payment;
    siteId?: number | null;
}

export interface BillImageModalResult {
    saved: boolean;
    attachmentId: string;
}

@Component({
    selector: 'bill-image-modal',
    template: `
        @if (serverAction) {
            <ws-busy-indicator></ws-busy-indicator>
        }
        <ws-modal-wrapper headerLabel="Bill Image" (save)="save()" (cancel)="cancel()">
            @if (!file) {
                <div style="height: 500px; text-align: center;">
                    @if (!fileError) {
                        <h3 style="padding-top: 200px;"><em>None Selected</em></h3>
                    }
                    @if (fileError) {
                        <h3 class="text-danger" style="padding-top: 200px;"><em>Error Loading Attachment</em></h3>
                    }
                </div>
            }
            <div style="display: flex;">
                @if (!fileError && file) {
                    <file-viewer [file]="file" style="width:100%; height: 500px;"> </file-viewer>
                }
            </div>
            <select
                class="form-select form-select-sm"
                style="margin-top: 25px;"
                [(ngModel)]="selectedAttachmentId"
                (change)="loadAttachment()"
            >
                @for (attachment of attachments; track attachment) {
                    <option [ngValue]="attachment.attachmentID">{{ attachment.description }}</option>
                }
            </select>
        </ws-modal-wrapper>
    `,
    standalone: false,
})
export class BillImageModalComponent
    implements OnInit, IWeissmanModalComponent<BillImageModalParams, BillImageModalResult>
{
    constructor(
        private readonly _attachmentService: AttachmentService,
        private _modalRef: BsModalRef,
        private _http: HttpClient,
        private readonly _documentService: DocumentService,
    ) {}

    params: BillImageModalParams;
    result: BillImageModalResult = {
        saved: false,
        attachmentId: '',
    };

    selectedAttachmentId: string;
    file: File;
    attachments: Attachment[];
    fileError: boolean;
    serverAction: boolean = false;

    async ngOnInit() {
        this.selectedAttachmentId = this.params.payment.attachmentID ? this.params.payment.attachmentID.toString() : '';

        this.serverAction = true;

        try {
            let attachments = await this._attachmentService.getAll(
                EntityType.TaxBill,
                this.params.billId,
                false,
                this.params.siteId,
            );

            attachments = map(attachments, (attachment) => {
                attachment.attachmentID = attachment.attachmentID.toString();
                attachment.description = attachment.description || 'No Description';
                return attachment;
            });

            attachments.unshift({
                attachmentID: '',
                description: '',
            } as Attachment);

            this.attachments = attachments;
            await this.loadAttachment();
        } finally {
            this.serverAction = false;
        }
    }

    async loadAttachment() {
        const selectedAttachment = find(this.attachments, { attachmentID: this.selectedAttachmentId });

        if (!selectedAttachment?.attachmentID) {
            this.file = undefined;
            return;
        }

        const { previewSupported, type } = this._attachmentService.getMimeTypeByFileExtension(
            selectedAttachment?.fileExtension,
        );

        try {
            const response = await this._documentService.getDocumentBody(selectedAttachment.attachmentID);
            const fileName = this._attachmentService.getFileNameFromHttpResponse(response);
            const fileNameType = fileName.split('.')[1].substring(0, 3);
            const blob = new Blob([response.body], { type: fileNameType });

            if (previewSupported) {
                this.file = new File([blob], fileName, { type });
            } else {
                await this._documentService.saveFile(blob, fileName);
            }
        } catch {
            this.fileError = true;
        }
    }

    async save() {
        this.serverAction = true;

        try {
            const request = this._http.patch('api/payment', {
                paymentID: this.params.payment.paymentID,
                attachmentID: this.selectedAttachmentId || null,
            });

            await lastValueFrom(request);
            this.result = {
                saved: true,
                attachmentId: this.selectedAttachmentId,
            };
            this._modalRef.hide();
        } finally {
            this.serverAction = false;
        }
    }

    cancel() {
        this._modalRef.hide();
    }
}
